import { useId, type ReactNode } from 'react';
import { Autocomplete, Box, Button, CircularProgress, Stack, type AutocompleteProps } from '@mui/material';
import { FormTextField } from './FormTextField';
import { InlineNotice } from './InlineNotice';
import { LoadingIndicator } from './LoadingIndicator';
import { visuallyHiddenStyles } from './visuallyHidden';

export type FormAutocompleteRequestState =
  | { status: 'idle' | 'loading' | 'ready'; message?: ReactNode }
  | { status: 'error'; message: ReactNode; onRetry?: () => void; retryLabel?: string };

export type FormAutocompleteInputProps = {
  label: ReactNode;
  help?: ReactNode;
  hideLabel?: boolean;
  labelSuffix?: ReactNode;
  placeholder?: string;
  helperText?: ReactNode;
  required?: boolean;
  error?: boolean;
  startAdornment?: ReactNode;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
};

export type FormAutocompleteProps<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
> = Omit<AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>, 'renderInput'> & {
  input: FormAutocompleteInputProps;
  request?: FormAutocompleteRequestState;
  selectionError?: ReactNode;
};

/**
 * 편집 form의 검색·선택 입력입니다. Field anatomy와 loading indicator를 공용 계약으로 소유합니다.
 * option 표현과 option equality 같은 업무 규칙은 호출자가 제공합니다.
 */
export function FormAutocomplete<
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
>({ input, loading = false, request, selectionError, ...autocompleteProps }: FormAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo>) {
  const requestMessageId = useId();
  const loadingLabelId = useId();
  const failedRequest = request?.status === 'error' ? request : undefined;
  const pending = request ? request.status === 'loading' : loading;
  const helperText = selectionError ?? input.helperText;
  const loadingLabel = request?.status === 'loading' ? request.message ?? '검색 중…' : autocompleteProps.loadingText ?? '검색 중…';
  return (
    <Stack spacing={0.75} sx={{ minWidth: 0 }}>
    <Autocomplete<Value, Multiple, DisableClearable, FreeSolo>
      {...autocompleteProps}
      data-loading={pending || undefined}
      forcePopupIcon={autocompleteProps.forcePopupIcon ?? (autocompleteProps.readOnly ? false : 'auto')}
      // 재시도 버튼으로 초점을 옮겨도 비동기 검색어를 지우지 않습니다.
      clearOnBlur={autocompleteProps.clearOnBlur ?? (request ? false : undefined)}
      options={failedRequest ? [] : autocompleteProps.options}
      loading={pending}
      loadingText={<Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
        <Box id={loadingLabelId} sx={visuallyHiddenStyles}>{loadingLabel}</Box>
        <LoadingIndicator labelledBy={loadingLabelId} />
      </Box>}
      noOptionsText={failedRequest ? '검색하지 못했습니다.' : request?.status === 'idle' ? request.message ?? '검색어를 입력해 주세요.' : autocompleteProps.noOptionsText ?? '검색 결과가 없습니다.'}
      renderInput={(params) => (
        <FormTextField
          {...params}
          label={input.label}
          help={input.help}
          hideLabel={input.hideLabel}
          labelSuffix={input.labelSuffix}
          placeholder={input.placeholder}
          helperText={helperText}
          required={input.required}
          error={input.error || Boolean(failedRequest || selectionError)}
          slotProps={{
            input: {
              ...params.slotProps.input,
              startAdornment: input.startAdornment === undefined
                ? params.slotProps.input.startAdornment
                : <>{input.startAdornment}{params.slotProps.input.startAdornment}</>,
              endAdornment: (
                <Box component="span" className="HeydayAutocomplete-actions">
                  {pending ? <Box component="span" className="HeydayAutocomplete-loading"><CircularProgress aria-hidden color="inherit" size={18} /></Box> : null}
                  {params.slotProps.input.endAdornment}
                </Box>
              ),
            },
            htmlInput: {
              ...params.slotProps.htmlInput,
              'aria-busy': pending || undefined,
              'aria-labelledby': input.ariaLabelledBy,
              'aria-describedby': [input.ariaDescribedBy, helperText == null ? undefined : `${params.id}-helper-text`, failedRequest ? requestMessageId : undefined].filter(Boolean).join(' ') || undefined,
            },
          }}
        />
      )}
    />
    {failedRequest ? <InlineNotice id={requestMessageId} severity="error" announcement="polite" density="compact" action={failedRequest.onRetry ? <Button size="small" disabled={autocompleteProps.disabled || autocompleteProps.readOnly} onClick={failedRequest.onRetry}>{failedRequest.retryLabel ?? '다시 시도'}</Button> : undefined}>{failedRequest.message}</InlineNotice> : null}
    </Stack>
  );
}
